import type { PipelineJobResponse } from '@/types/pipeline';
import type { ReviewSessionDetailResponse } from '@/types/review';
import type { SessionStatus } from '@/types/session';

export const previewUsers = [
  {
    id: '00000000-0000-4000-a000-000000000001',
    email: 'preview-admin@test.local',
    name: 'Preview Admin',
    cognito_sub: 'preview-admin-sub',
    cognito_status: 'CONFIRMED',
    last_login_at: '2026-05-12T13:45:00+09:00',
    is_feedbacker: true,
    is_admin: true,
    default_reviewer_name: null,
  },
  {
    id: '00000000-0000-4000-a000-000000000002',
    email: 'preview-coach@test.local',
    name: 'QA Coach 1',
    cognito_sub: 'preview-coach-sub',
    cognito_status: 'FORCE_CHANGE_PASSWORD',
    last_login_at: null,
    is_feedbacker: false,
    is_admin: false,
    default_reviewer_name: 'Preview Admin',
  },
  {
    id: '00000000-0000-4000-a000-000000000003',
    email: 'preview-feedbacker@test.local',
    name: 'QA Feedbacker 1',
    cognito_sub: 'preview-feedbacker-sub',
    cognito_status: 'CONFIRMED',
    last_login_at: '2026-05-11T18:20:00+09:00',
    is_feedbacker: true,
    is_admin: false,
    default_reviewer_name: 'Preview Admin',
  },
];

export const previewFeedbackers = previewUsers
  .filter((u) => u.is_feedbacker)
  .map((u) => ({ id: u.id, name: u.name, email: u.email }));

export const previewClients = [
  {
    id: '00000000-0000-4000-b000-000000000001',
    name: 'QA Client A',
    owner_coach_id: '00000000-0000-4000-a000-000000000002',
    coach_name: 'QA Coach 1',
    feedbacker_count: 2,
    information_disclosure_agreement_uploaded: true,
    information_disclosure_agreement_filename: 'agreement-a.pdf',
    information_disclosure_agreement_uploaded_at: '2026-05-30T00:00:00Z',
    information_disclosure_agreements: [
      {
        id: '00000000-0000-4000-d000-000000000001',
        filename: 'agreement-a.pdf',
        content_type: 'application/pdf',
        uploaded_at: '2026-05-30T00:00:00Z',
      },
    ],
  },
  {
    id: '00000000-0000-4000-b000-000000000002',
    name: 'QA Client B',
    owner_coach_id: '00000000-0000-4000-a000-000000000002',
    coach_name: 'QA Coach 1',
    feedbacker_count: 1,
    information_disclosure_agreement_uploaded: false,
    information_disclosure_agreement_filename: null,
    information_disclosure_agreement_uploaded_at: null,
    information_disclosure_agreements: [],
  },
];

const statusRows: Array<{ status: SessionStatus; day: string; action: boolean }> = [
  { status: 'transcribing', day: 'Day1', action: false },
  { status: 'section_config_pending', day: 'Day1', action: true },
  { status: 'ai_evaluating', day: 'Day2', action: false },
  { status: 'feedback_pending', day: 'Day1', action: true },
  { status: 'feedback_in_progress', day: 'Day3', action: true },
  { status: 'coach_ack_pending', day: 'Day1', action: true },
  { status: 'completed', day: 'DayX', action: false },
  { status: 'transcribing_failed', day: 'Day2', action: false },
  { status: 'ai_evaluation_failed', day: 'Day3', action: false },
];

export const previewSessions = statusRows.map((row, index) => {
  const serial = index + 1;
  const client = index > 6 ? previewClients[1]! : previewClients[0]!;
  return {
    id: `00000000-0000-4000-c000-${serial.toString().padStart(12, '0')}`,
    client_id: client.id,
    client_name: client.name,
    coach_user_id: '00000000-0000-4000-a000-000000000002',
    coach_name: 'QA Coach 1',
    day_type: row.day,
    serial_no: serial,
    status: row.status,
    outcome: row.status === 'completed' ? 'human_feedback_provided' : null,
    current_section_version: 1,
    session_date: `2026-04-${(20 + serial).toString().padStart(2, '0')}`,
    created_at: `2026-04-${(20 + serial).toString().padStart(2, '0')}T09:00:00+09:00`,
    updated_at: `2026-04-${(20 + serial).toString().padStart(2, '0')}T10:30:00+09:00`,
    requires_my_action: row.action,
  };
});

export const previewReviewers = [
  {
    reviewer_user_id: '00000000-0000-4000-a000-000000000001',
    reviewer_name: 'Preview Admin',
    state: 'pending',
    assigned_at: '2026-04-28T09:00:00+09:00',
  },
  {
    reviewer_user_id: '00000000-0000-4000-a000-000000000003',
    reviewer_name: 'QA Feedbacker 1',
    state: 'in_progress',
    assigned_at: '2026-04-27T09:00:00+09:00',
  },
];

export const previewReviewDetail: ReviewSessionDetailResponse = {
  session: {
    session_id: '00000000-0000-4000-c000-000000000004',
    display_name: 'QA Client A / Day1 #4',
    coach_name: 'QA Coach 1',
    client_name: 'QA Client A',
    meeting_summary:
      '会議では各部門のKGI・KPI設定が主な議題。\nマーケ課：KGIは「着地数」、KPIは「送信数」と「獲得率」に決定。送信数は獲得率より根本指標であり再現性が高いとの理由から優先された。\n営業課：KGIは「単月売上」、KPIは「着地成約率」「人員」に決定。アポ成約率では着地有無に数字が左右されるため、着地後の成約率を追う形に変更。\nカスタマーサクセス：継続率10%と低く顧客満足度も課題。KPIは「提案数」中心で議論。営業とコンサル間の期待値コントロールや立場の弱さも問題として挙がった。\n全体を通じ、各指標の定義（着地・アポ・提案の意味）を統一しながら意思決定を進めた。',
    day: 'Day1',
    date: '2026-04-24',
    legacy_result: 'WARNING',
    issue_count: 2,
    data_kind: 'real',
    reviewed_section_count: 2,
    reviewed_scenario_count: 3,
    semantic_status: 'WARNING',
    latest_evaluated_at: '2026-04-24T15:00:00+09:00',
  },
  transcript_blocks: [
    {
      name: 'お約束',
      start_time: '00:00:00',
      end_time: '00:01:10',
      utterances: [
        {
          sequence_index: 0,
          utterance_id: 'u0001',
          speaker: 'facilitator',
          timestamp: '00:00:00',
          text: '本日はお集まりいただきありがとうございます。今日の3つのSを確認します。',
          block_name: 'お約束',
        },
        {
          sequence_index: 1,
          utterance_id: 'u0002',
          speaker: 'participant',
          timestamp: '00:00:08',
          text: 'Short, Simple, Straight ですね。',
          block_name: 'お約束',
        },
        {
          sequence_index: 2,
          utterance_id: 'u0003',
          speaker: 'facilitator',
          timestamp: '00:00:15',
          text: 'そうです。ではお互いの期待を共有しましょう。',
          block_name: 'お約束',
        },
        {
          sequence_index: 3,
          utterance_id: 'u0004',
          speaker: 'participant',
          timestamp: '00:00:25',
          text: 'わかりません。',
          block_name: 'お約束',
        },
        {
          sequence_index: 4,
          utterance_id: 'u0005',
          speaker: 'facilitator',
          timestamp: '00:00:30',
          text: '了解しました。次に進みましょう。',
          block_name: 'お約束',
        },
      ],
    },
    {
      name: '期待',
      start_time: '00:01:10',
      end_time: '00:02:00',
      utterances: [
        {
          sequence_index: 5,
          utterance_id: 'u0006',
          speaker: 'facilitator',
          timestamp: '00:01:12',
          text: 'この会議に期待している成果を、短く一人ずつお願いします。',
          block_name: '期待',
        },
        {
          sequence_index: 6,
          utterance_id: 'u0007',
          speaker: 'participant',
          timestamp: '00:01:22',
          text: '営業の打ち手を明確にしたいです。',
          block_name: '期待',
        },
      ],
    },
  ],
  session_feedback_items: [
    {
      feedback_key: 'fb-preview-001',
      scenario_id: 'scenario-promise-001',
      section_display_name: 'お約束',
      item_id: 'ITEM-001',
      item_name: 'わかりません許容',
      section_name: 'promise',
      lane: 'must',
      axis: 'script',
      status: 'ISSUE',
      utterance_id: 'u0004',
      primary_utterance_id: 'u0004',
      supporting_utterance_ids: ['u0005'],
      sequence_index: 0,
      timestamp: '00:00:25',
      speaker: 'participant',
      block_name: 'お約束',
      quote: 'わかりません。',
      primary_quote: 'わかりません。',
      supporting_quotes: ['了解しました。次に進みましょう。'],
      reason: '参加者の「わかりません」を確認したまま進行したため、別の聞き方で再質問する必要がある。',
      recommendation: '「今の時点で一番近い答えは何ですか？」のように、答えられる形に問い直す。',
      source_refs: [],
      my_judgment: 'NG',
      my_comment_body: 'ここは指摘どおり。再質問の具体例を添えるとコーチが行動に移しやすいです。',
      reviewer_judgments: [
        {
          reviewer_user_id: '00000000-0000-4000-a000-000000000003',
          reviewer_name: 'QA Feedbacker 1',
          judgment: 'NG',
          comment_body: '再質問して回答を引き出してほしいです。',
          reviewed_at: '2026-04-24T16:00:00+09:00',
        },
      ],
    },
    {
      feedback_key: 'fb-preview-002',
      scenario_id: 'scenario-expectation-001',
      section_display_name: '期待',
      item_id: 'ITEM-002',
      item_name: '期待の書き出し促進',
      section_name: 'expectation',
      lane: 'advisory',
      axis: 'meaning',
      status: 'CHECK',
      utterance_id: 'u0006',
      primary_utterance_id: 'u0006',
      supporting_utterance_ids: ['u0007'],
      sequence_index: 1,
      timestamp: '00:01:12',
      speaker: 'facilitator',
      block_name: '期待',
      quote: '短く一人ずつお願いします。',
      primary_quote: '短く一人ずつお願いします。',
      supporting_quotes: ['営業の打ち手を明確にしたいです。'],
      reason: '期待を出す導入はできているが、書き出しやフォーマットの指定が弱い。',
      recommendation: '発言前に「まず紙に一文で書いてください」と明示してから共有に入る。',
      source_refs: [],
      my_judgment: null,
      my_comment_body: '',
    },
  ],
  additional_feedbacks: [
    {
      id: 'af-preview-001',
      utterance_id: 'u0003',
      sequence_index: 2,
      timestamp: '00:00:15',
      speaker: 'facilitator',
      block_name: 'お約束',
      quote: 'そうです。ではお互いの期待を共有しましょう。',
      author_name: 'QA Feedbacker 1',
      comment_body:
        'AI評価には出ていませんが、次のセクションへ移る前の間が短く、参加者が考える時間を取りにくそうでした。',
      created_at: '2026-04-24 16:05',
      reviewer_user_id: '00000000-0000-4000-a000-000000000003',
      can_delete: true,
      can_edit: true,
    },
  ],
  review_sections: [],
};

export const previewTranscription = {
  utterances: previewReviewDetail.transcript_blocks.flatMap((block) =>
    block.utterances.map((u) => ({
      id: u.utterance_id,
      speaker: u.speaker,
      speaker_name: u.speaker === 'facilitator' ? 'コーチ' : '参加者',
      text: u.text,
      start_ms: u.sequence_index * 10_000,
      end_ms: u.sequence_index * 10_000 + 8_000,
      block_name: block.name,
    })),
  ),
};

export const previewPipelineJobs: PipelineJobResponse[] = [
  {
    job_id: '00000000-0000-4000-d000-000000000004',
    status: 'section_pending',
    day: 'Day1',
    cycle: 1,
    original_filename: 'preview-audio.wav',
    audio_s3_keys: { mixed: ['preview/audio.wav'] },
    created_at: '2026-04-24T09:00:00+09:00',
    updated_at: '2026-04-24T09:10:00+09:00',
    progress: { stage: 'section_pending', detail: 'UI preview: セクション設定待ち' },
  },
  {
    job_id: '00000000-0000-4000-d000-000000000006',
    status: 'completed',
    day: 'Day1',
    cycle: 1,
    original_filename: 'preview-completed.wav',
    audio_s3_keys: { mixed: ['preview/completed.wav'] },
    created_at: '2026-04-23T09:00:00+09:00',
    updated_at: '2026-04-23T09:30:00+09:00',
    completed_at: '2026-04-23T09:30:00+09:00',
    progress: { stage: 'completed', detail: 'UI preview: 完了' },
    result: {
      transcription_s3_key: 'preview/transcription.json',
      evaluation_s3_key: 'preview/evaluation.json',
      evaluation_summary: { evaluation_result: 'WARNING', issue_count: 2, participant_ng_count: 1 },
      blocks_detected: ['お約束', '期待'],
    },
  },
];
